import { useCallback, useEffect, useState } from 'react';

import { unique } from '@revenuemarket/utils/arrayUtils';
import { MenuInfo, SelectInfo } from 'rc-menu/lib/interface';

import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult } from '~components/ui/HorizontalTreeTable/Table';
import { CashFlowType } from '~models/cashFlow';

import SearchMenuList from './common/component/SearchMenuList';

interface Props {
  activeKey?: string[] | null;
  searchValue: string;
  treeList: FlattenTreeResult<GroupTreeItem>[];
  setActiveItem: (item: FlattenTreeResult<GroupTreeItem>) => void;
  excludeRoot?: boolean;
  cashFlowType?: CashFlowType;
  /** 비활성화 아이템도 action이 가능히게 하려면 disableActionOnInactiveItem = false (ex: 분류 관리 page) */
  disableActionOnInactiveItem?: boolean;
  onSelectFinish?: (treeKey: string) => void;
  multiple?: boolean;
}

const GroupSearchList: React.FC<Props> = ({
  activeKey,
  searchValue,
  setActiveItem,
  excludeRoot,
  treeList,
  disableActionOnInactiveItem = true,
  onSelectFinish,
  multiple = false,
}) => {
  const [selectedKey, setSelectedKey] = useState<(string | number)[] | null>(activeKey || null);

  const flattenedTree = treeList.filter((item) => {
    return (excludeRoot ? item.depth >= 0 : true) && !item?.isDisabled && item.status !== 'new';
  });

  const searchResults = flattenedTree.filter(
    (item) => item.namePath?.toLocaleLowerCase()?.includes(searchValue?.toLocaleLowerCase().trim()),
  );

  const onSelect = useCallback(
    (item: SelectInfo | MenuInfo) => {
      setActiveItem(
        flattenedTree.find((i) => i.key === item.key) as FlattenTreeResult<GroupTreeItem>,
      );
      if (multiple) {
        const selectedKey = unique([...(activeKey ?? []), item.key]);
        setSelectedKey(selectedKey);
      } else {
        setSelectedKey([item.key]);
      }

      onSelectFinish?.(item.key);
    },
    [activeKey, setActiveItem, flattenedTree, multiple, onSelectFinish],
  );

  useEffect(() => {
    if (activeKey) {
      setSelectedKey(activeKey);
    }
  }, [activeKey]);

  return (
    <SearchMenuList<GroupTreeItem>
      onSelect={onSelect}
      searchResults={searchResults}
      selectedKey={selectedKey}
      disableActionOnInactiveItem={disableActionOnInactiveItem}
    />
  );
};

export default GroupSearchList;
